import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link to="/category" className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all inline-block no-underline">
              浏览更多文章
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    articleBody: article.content.replace(/<[^>]*>/g, ''),
    publisher: {
      '@type': 'Organization',
      name: '眠境',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://sleepono.github.io/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://sleepono.github.io/category?tab=${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://sleepono.github.io/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors no-underline">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category?tab=${article.category}`} className="hover:text-primary-600 transition-colors no-underline">{article.categoryName}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                {article.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}
                </span>
              </div>
            </header>

            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 助眠 ASMR 深度指南`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground-700
                prose-strong:text-foreground-950
                [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:text-sm md:[&_p]:text-base [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                [&_strong]:text-foreground-950"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        title={`${rel.title} - 助眠 ASMR`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-accent-600 font-medium">{rel.categoryName}</span>
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}